import { autoinject, bindable, computedFrom, observable } from 'aurelia-framework';
import { NotificationType, ResourcedNotificationService } from '../../components/notifications/notification-service';
import { ResponseHandler } from '../../utils/response-handler';
import { IRuleValidationException } from './accounting-rule-error-display';
import { AccountingRuleApi, IAddRuleAM } from "./accounting-rule-api";
import { AccountingRule, AccountingRuleFolder, IAccountingRuleFolder, RuleTemplate } from './accounting-rule-models';
import { last } from 'lodash';

@autoinject
export class AddAccountingRuleCore {
	@bindable scopeId: string;
	@bindable scopeIsClosed: boolean;
	@bindable onInsertSucceeded;
	@bindable ruleType: string;
	@bindable definition: string;

	@bindable allowChangeOfRuleType: boolean = false;
	@bindable sourceRule: AccountingRule;
	@observable newRuleType: string;

	@bindable folders: IAccountingRuleFolder[];
	@bindable selectedFolder: IAccountingRuleFolder; // When creating a sibling rule, selected folder is bound to the sibling rule's folder

	validationErrors: IRuleValidationException[];
	insertingNewRule = false;

	@computedFrom('ruleType')
	get showDescriptionOnly() : boolean {
		return this.ruleType === 'AutomaticTypeRecognition'; 
	}

	constructor(private api: AccountingRuleApi, private responseHandler: ResponseHandler, private notificationService: ResourcedNotificationService) {
	}

	attached() {
		if (!this.definition && this.sourceRule) {
			this.definition = this.generateDefinition(this.ruleType);
		}

		if (this.folders) {
			this.selectedFolder = this.folders.find(f => f.id === 'rootFolder');
		}

		if (this.allowChangeOfRuleType && this.ruleType) {
			this.newRuleType = this.ruleType;
		}
	}

	newRuleTypeChanged() {
		if (this.allowChangeOfRuleType) {
			this.definition = this.generateDefinition(this.newRuleType);
		}
	}

	private generateDefinition(type: string): string {
		const definition = RuleTemplate.getForType(type)
			.replace('<COSTCENTER>', `costcenter ${this.sourceRule.costCenters.map(cc => cc.identifier).join(", ")}`)
			.replace('<MUNICIPALITIES>', '    ')
			.replace('<SERVICE>', '')
			.replace('<SERVICES>', '    ');

		return definition;
	}

	insertNewRule() {
		if (this.scopeIsClosed) {
			this.notificationService.push('ViewMessage_OperationNotAllowed', NotificationType.Error, null, ['kausi on suljettu'], null);
			return; 
		}

		this.insertingNewRule = true;

		let newRule: IAddRuleAM;
		if (this.sourceRule) {
			newRule = {
				scopeId: this.scopeId,
				typeName: this.newRuleType,
				definition: this.definition,
				folderId: this.selectedFolder.id === 'rootFolder' ? null : this.selectedFolder.id,
				orderNumber: this.sourceRule.orderNumber + 1
			};
		} else {
			const orderNumber = AccountingRuleFolder.getLastOrderNumber(this.selectedFolder);
			newRule = {
				scopeId: this.scopeId,
				typeName: this.ruleType,
				definition: this.definition,
				folderId: this.selectedFolder.id === 'rootFolder' ? null : this.selectedFolder.id,
				orderNumber
			};
		}
			
		newRule.definition = AccountingRule.formatDefinition(newRule.definition);
		
		this.api.addNewRule(newRule).then(response => {
			if (this.responseHandler.isClientError(response.status)) {
				response.json().then(errors => {
					this.validationErrors = errors;
				});
			} else if (this.responseHandler.isSuccess(response.status)) {
				response.json().then(addedRule => {
					if (this.onInsertSucceeded) {
						this.onInsertSucceeded({ addedRule: addedRule });
					}
				});
			}
			this.insertingNewRule = false;
		});
	}
}
